import { useCallback, useState } from 'react';
import { toast } from 'react-toastify';
import DocumentService from '../../../../Services/documents.service';
import Constants from '../../../../assests/Constants/constants';

const isVatExpenseType = (type) => String(type || '').includes('תשומה');
const isVatSaleType = (type) => String(type || '').includes('עסקה');

const guessVatPercentFromAmounts = (vatAmount, invoiceAmount) => {
    const inv = Math.abs(Number(invoiceAmount));
    const vat = Math.abs(Number(vatAmount));
    if (!inv || !vat || inv <= vat) return null;
    return Math.round((vat / (inv - vat)) * 1000) / 10;
};

const toDay10Iso = (dateStr) => {
    if (!dateStr) return new Date().toISOString();
    const d = new Date(dateStr);
    if (Number.isNaN(d.getTime())) return new Date().toISOString();
    return new Date(d.getFullYear(), d.getMonth(), 10).toISOString();
};

export const isVatRowReady = (row) =>
    !!row &&
    row.status !== 'added' &&
    !!row.sortCodeId &&
    !!row.debitAccount &&
    !!row.creditAccount &&
    row.invoiceAmount != null &&
    Number(row.invoiceAmount) !== 0;

/**
 * Shared state for the "VAT report rows that have no matching journal line" table:
 * pre-fills a sort code / debit / credit per row and creates the journal entries.
 * Used by both the MDB and the Ramplus journal imports.
 */
const useMissingVatReportRows = ({
    client,
    token,
    t,
    sortCodeOptions = [],
    getRegistrationMonthSource,
}) => {
    const [rows, setRows] = useState([]);
    const [selectedIds, setSelectedIds] = useState([]);
    const [adding, setAdding] = useState(false);

    const pickSortCodeForVatRow = useCallback((row) => {
        const codes = (sortCodeOptions || []).filter(sc => sc?.sortCodeId);
        if (!codes.length) return null;
        const preferType = isVatExpenseType(row.transactionType)
            ? Constants.SortCodeVatTypeValue?.VatExpenses ?? 2
            : isVatSaleType(row.transactionType)
                ? Constants.SortCodeVatTypeValue?.VatTransactions ?? 1
                : null;
        let candidates = codes;
        if (preferType != null) {
            const typed = codes.filter(sc => Number(sc.sortCodeVatType) === preferType);
            if (typed.length) candidates = typed;
        }
        const vatPct = guessVatPercentFromAmounts(row.vatAmount, row.invoiceAmount);
        if (vatPct != null) {
            const byVat = candidates.filter(sc => Math.abs(Number(sc.vat) - vatPct) <= 1.5);
            if (byVat.length) return byVat[0];
        }
        return candidates[0] || null;
    }, [sortCodeOptions]);

    const setRowsFromPreview = useCallback((missingVatReportRows) => {
        const built = (missingVatReportRows || []).map((row, index) => {
            const sortCode = pickSortCodeForVatRow(row);
            const accountKey = row.accountKey ? String(row.accountKey) : '';
            return {
                id: `${row.fileName || 'vat'}-${row.transactionNumber || row.reference || index}-${index}`,
                fileName: row.fileName,
                reportingMonth: row.reportingMonth,
                transactionNumber: row.transactionNumber,
                reference: row.reference,
                documentDate: row.documentDate,
                accountKey: row.accountKey,
                transactionType: row.transactionType,
                vatAmount: row.vatAmount,
                invoiceAmount: row.invoiceAmount,
                sortCodeId: sortCode?.sortCodeId || '',
                debitAccount: accountKey,
                creditAccount: '',
                vat: sortCode?.vat ?? guessVatPercentFromAmounts(row.vatAmount, row.invoiceAmount) ?? '',
                status: 'pending',
                error: null,
            };
        });
        setRows(built);
        setSelectedIds([]);
    }, [pickSortCodeForVatRow]);

    const updateRow = useCallback((id, field, value) => {
        setRows(prev => prev.map(row => {
            if (row.id !== id) return row;
            const next = { ...row, [field]: value, status: row.status === 'added' ? row.status : 'pending', error: null };
            if (field === 'sortCodeId') {
                const sc = (sortCodeOptions || []).find(o => String(o.sortCodeId) === String(value));
                if (sc && sc.vat != null) next.vat = sc.vat;
            }
            return next;
        }));
    }, [sortCodeOptions]);

    const reset = useCallback(() => {
        setRows([]);
        setSelectedIds([]);
        setAdding(false);
    }, []);

    const createJournalFromVatRow = useCallback(async (row) => {
        if (!client || !token || !isVatRowReady(row)) {
            throw new Error(t('FillSortCodeDebitCredit') || 'נא למלא קוד מיון, חובה וזכות');
        }
        const amount = Math.abs(Number(row.invoiceAmount) || 0);
        const documentDate = row.documentDate
            ? new Date(row.documentDate).toISOString()
            : new Date().toISOString();
        const reportingMonthDate = row.reportingMonth
            ? toDay10Iso(row.reportingMonth)
            : toDay10Iso(row.documentDate);
        const registrationMonthDate = getRegistrationMonthSource?.() === 'ReportingMonth'
            ? reportingMonthDate
            : toDay10Iso(row.documentDate || row.reportingMonth);
        const sortCode = (sortCodeOptions || []).find(o => String(o.sortCodeId) === String(row.sortCodeId));
        const payload = {
            caseId: client.caseId,
            accountantId: client.accountantId,
            sortCodeId: row.sortCodeId,
            reference: row.reference != null ? String(row.reference) : '',
            description: row.transactionType
                ? `${row.transactionType} - ${row.reference || ''}`.trim()
                : (t('AddedFromVatReport') || 'נוסף מדוח מע״מ'),
            amount,
            documentDate,
            uploadDate: new Date().toISOString(),
            reportingMonthDate,
            registrationMonthDate,
            creditAccount: row.creditAccount,
            debitAccount: row.debitAccount,
            vat: row.vat !== '' && row.vat != null ? Number(row.vat) : (sortCode?.vat ?? 0),
            addAsProcessed: false,
        };
        const response = await DocumentService.UploadDocumentPayload('JournalEntries', payload, token);
        if (!response.ok) {
            const text = await response.text();
            throw new Error(text || t('FailedToCreateJournalEntry') || 'Failed to create journal entry');
        }
        return response;
    }, [client, token, t, sortCodeOptions, getRegistrationMonthSource]);

    const addRows = useCallback(async (rowIds) => {
        const ids = rowIds?.length ? rowIds : selectedIds;
        const targets = rows.filter(r => ids.includes(r.id) && isVatRowReady(r));
        if (!targets.length) {
            toast.warning(t('SelectReadyVatRows') || 'בחרו שורות עם קוד מיון, חובה וזכות');
            return { success: 0, failed: 0 };
        }
        setAdding(true);
        let success = 0;
        let failed = 0;
        for (const row of targets) {
            setRows(prev => prev.map(r => (r.id === row.id ? { ...r, status: 'adding', error: null } : r)));
            try {
                await createJournalFromVatRow(row);
                success += 1;
                setRows(prev => prev.map(r => (r.id === row.id ? { ...r, status: 'added', error: null } : r)));
                setSelectedIds(prev => prev.filter(id => id !== row.id));
            } catch (err) {
                failed += 1;
                setRows(prev => prev.map(r => (r.id === row.id
                    ? { ...r, status: 'error', error: err?.message || String(err) }
                    : r)));
            }
        }
        setAdding(false);
        if (success > 0) {
            toast.success(t('VatRowsAddedCount', { count: success }) || `נוספו ${success} פקודות יומן`);
        }
        if (failed > 0) {
            toast.error(t('VatRowsFailedCount', { count: failed }) || `${failed} שורות נכשלו`);
        }
        return { success, failed };
    }, [rows, selectedIds, createJournalFromVatRow, t]);

    return {
        rows,
        selectedIds,
        setSelectedIds,
        adding,
        setRowsFromPreview,
        updateRow,
        addRows,
        reset,
    };
};

export default useMissingVatReportRows;
